import React, { useState, useCallback, useMemo, useEffect } from "react";
import Modal from "@components/common/Modal/Modal";
import Button from "@components/common/Button/Button";
import Input from "@components/common/Input/Input";
import Htag from "@components/common/Htag/Htag";
import P from "@components/common/P/P";
import SvgIcon from "@components/common/SvgIcon/SvgIcon";
import {
	CarrierIcon,
	CarrierThreeIcon,
	CarrierTwoIcon,
	GoogleSignInIcon,
	LogoLightNavIcon,
	SenderIcon,
} from "@assets/icons";
import { useToast } from "context/ToastContext";

import Spinner from "@components/Spinner";
import { motion } from "framer-motion";
import Checkbox from "@components/common/Checkbox/Checkbox";
import { ROUTES } from "constants/routes";
import { useNavigate } from "react-router-dom";
import {
	loginThunk,
	registerRequestThunk,
	registerThunk,
	validateOtpThunk,
} from "store/thunks/authThunks";
import { useDispatch } from "react-redux";
import { AppDispatch } from "store/store";
import { useTranslation } from "react-i18next";

interface RegisterProcessProps {
	isModalOpen: boolean;
	onClose: () => void;
}

const RegisterProcess: React.FC<RegisterProcessProps> = ({
	isModalOpen,
	onClose,
}) => {
	const dispatch = useDispatch<AppDispatch>();
	const navigate = useNavigate();
	const { t } = useTranslation();
	const [step, setStep] = useState(1);
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [roleID, setRoleID] = useState<number | null>(null);

	const [otp, setOtp] = useState("");
	const [isLoading, setLoading] = useState(false);
	const [isChecked, setIsChecked] = useState(false);
	const [usePhone, setUsePhone] = useState(false); // State to track if phone input is used
	const [phone, setPhone] = useState(""); // State for phone input

	const { showToast } = useToast();

	useEffect(() => {
		console.log("[RegisterProcess] mounted");
		return () => console.log("[RegisterProcess] unmounted");
	}, []);

	useEffect(() => {
		console.log("[RegisterProcess] isModalOpen:", isModalOpen);
	}, [isModalOpen]);

	useEffect(() => {
		console.log("[RegisterProcess] step:", step);
	}, [step]);

	useEffect(() => {
		if (isModalOpen) {
			setStep(1);
			setEmail("");
			setPassword("");
			setRoleID(null);
			setOtp("");
			setIsChecked(false);
		}
	}, [isModalOpen]);

	// handler
	// inside RegisterProcess
	const handleRegisterRequest = useCallback(() => {
		if (!roleID) {
			showToast({
				message: t("auth.register.toast.roleRequired"),
				status: "error",
				position: "top-right",
				autoClose: true,
				duration: 3000,
			});
			return;
		}

		const contact = usePhone ? phone.trim() : email.trim();
		if (!contact) {
			showToast({
				message: usePhone
					? t("auth.register.toast.enterPhone")
					: t("auth.register.toast.enterEmail"),
				status: "error",
				position: "top-right",
				autoClose: true,
				duration: 3000,
			});
			return;
		}

		const payload = {
			role_id: roleID,
			...(usePhone ? { phone: contact } : { email: contact }),
		};

		setLoading(true);
		dispatch(registerRequestThunk(payload))
			.unwrap()
			.then(() => {
				showToast({
					message: t("auth.register.toast.otpSent"),
					status: "success",
					position: "top-right",
					autoClose: true,
					duration: 3000,
				});
				setStep(4);
			})
			.catch((error: Error) => {
				showToast({
					message: error.message || t("auth.register.toast.otpSendFailed"),
					status: "error",
					position: "top-right",
					autoClose: true,
					duration: 3000,
				});
			})
			.finally(() => setLoading(false));
	}, [dispatch, roleID, usePhone, phone, email, showToast, t]);

	const handleValidateOtp = useCallback(() => {
		setLoading(true);

		const payload = {
			otp,
			...(usePhone ? { phone: phone.trim() } : { email: email.trim() }),
		};

		dispatch(validateOtpThunk(payload))
			.unwrap()
			.then(() => {
				showToast({
					message: t("auth.register.toast.otpVerified"),
					status: "success",
					position: "top-right",
					autoClose: true,
					duration: 3000,
				});
				setStep(5);
			})
			.catch((error: Error) => {
				showToast({
					message: error.message || t("auth.register.toast.otpVerifyFailed"),
					status: "error",
					position: "top-right",
					autoClose: true,
					duration: 3000,
				});
			})
			.finally(() => setLoading(false));
	}, [dispatch, otp, usePhone, phone, email, showToast, t]);

	const handleFinalRegistration = useCallback(async () => {
		const contact = usePhone ? phone.trim() : email.trim();

		// validations...
		if (!roleID) {
			showToast({
				message: t("auth.register.toast.pickUserType"),
				status: "error",
				position: "top-right",
				autoClose: true,
				duration: 3000,
			});
			return;
		}
		if (!contact) {
			showToast({
				message: usePhone
					? t("auth.register.toast.enterPhone")
					: t("auth.register.toast.enterEmail"),
				status: "error",
				position: "top-right",
				autoClose: true,
				duration: 3000,
			});
			return;
		}
		if (!otp) {
			showToast({
				message: t("auth.register.toast.enterOtp"),
				status: "error",
				position: "top-right",
				autoClose: true,
				duration: 3000,
			});
			return;
		}
		if (!password || !confirmPassword || password !== confirmPassword) {
			showToast({
				message:
					!password || !confirmPassword
						? t("auth.register.toast.enterAndConfirmPassword")
						: t("auth.register.toast.passwordsMismatch"),
				status: "error",
				position: "top-right",
				autoClose: true,
				duration: 3000,
			});
			return;
		}

		// ✅ prevent double click
		if (isLoading) return;

		const payload = {
			roleID: roleID!,
			password,
			otp,
			...(usePhone ? { phone: contact } : { email: contact }),
		};

		setLoading(true);
		try {
			await dispatch(registerThunk(payload)).unwrap();

			showToast({
				message: t("auth.register.toast.registerSuccess"),
				status: "success",
				position: "top-right",
				autoClose: true,
				duration: 3000,
			});

			await dispatch(
				loginThunk({
					username: contact,
					password,
					credType: usePhone ? "phone" : "email",
				}),
			).unwrap();

			navigate(ROUTES.MAIN);
			onClose();
		} catch (error: any) {
			showToast({
				message:
					error?.message || t("auth.register.toast.registerOrLoginFailed"),
				status: "error",
				position: "top-right",
				autoClose: true,
				duration: 3000,
			});
		} finally {
			setLoading(false);
		}
	}, [
		dispatch,
		roleID,
		usePhone,
		phone,
		email,
		otp,
		password,
		confirmPassword,
		isLoading,
		showToast,
		onClose,
		navigate,
		t,
	]);

	const stepVariant = {
		initial: { opacity: 0, x: -30 },
		animate: { opacity: 1, x: 0 },
		exit: { opacity: 0, x: 50 },
	};

	const renderStep = useMemo(
		() => (
			<motion.div
				key={step}
				variants={stepVariant}
				initial="initial"
				animate="animate"
				exit="exit"
				transition={{ duration: 0.3 }}
			>
				{step === 1 ? (
					<div className="grid grid-flow-row grid-cols-1 gap-5 auto-rows-max">
						<div className="grid grid-flow-row grid-cols-1 gap-1 auto-rows-max">
							<Htag tag="h3" type="semibold" className="text-center">
								{t("auth.register.welcome")}
							</Htag>
							<P size="p2" type="regular">
								{t("auth.register.whichUserType")}
							</P>
						</div>
						<div className="grid gap-8 grid-cols-maxThree place-items-center place-content-center">
							<div
								className={`grid grid-cols-1 grid-flow-row auto-rows-max gap-4 place-items-center`}
								onClick={() => setRoleID(3)}
							>
								<div
									className={`border p-4 rounded-xl cursor-pointer ${roleID === 3 ? "border-light-mainColor dark:border-dark-mainColor" : "border-light-stroke dark:border-dark-stroke"}`}
								>
									<SvgIcon
										Icon={SenderIcon}
										width={46}
										height={46}
										className={`${roleID === 3 ? "fill-light-mainColor dark:fill-dark-mainColor" : "fill-light-grayThree dark:fill-dark-grayThree"}`}
									/>
								</div>

								<div className="grid grid-flow-row grid-cols-1 gap-1">
									<P
										size="p3"
										type="semibold"
										className={`text-center ${roleID === 3 ? "text-light-mainColor dark:text-dark-mainColor" : "text-light-textColor dark:text-dark-textColor"}`}
									>
										{t("auth.register.roles.sender.title")}
									</P>
									<P
										size="t1"
										type="regular"
										className={`text-center ${roleID === 3 ? "text-light-mainColor dark:text-dark-mainColor" : "text-light-grayThree dark:text-dark-grayThree"}`}
									>
										{t("auth.register.roles.sender.subtitle")}
									</P>
								</div>
							</div>

							<div
								className={`grid grid-cols-1 grid-flow-row auto-rows-max gap-4 place-items-center`}
								onClick={() => setRoleID(2)}
							>
								<div
									className={`border p-4 rounded-xl cursor-pointer ${roleID === 2 ? "border-light-mainColor dark:border-dark-mainColor" : "border-light-stroke dark:border-dark-stroke"}`}
								>
									<SvgIcon
										Icon={CarrierIcon}
										width={46}
										height={46}
										className={`${roleID === 2 ? "fill-light-mainColor dark:fill-dark-mainColor" : "fill-light-grayThree dark:fill-dark-grayThree"}`}
									/>
								</div>
								<div className="grid grid-flow-row grid-cols-1 gap-1">
									<P
										size="p3"
										type="semibold"
										className={`text-center ${roleID === 2 ? "text-light-mainColor dark:text-dark-mainColor" : "text-light-textColor dark:text-dark-textColor"}`}
									>
										{t("auth.register.roles.carrier.title")}
									</P>
									<P
										size="t1"
										type="regular"
										className={`text-center ${roleID === 2 ? "text-light-mainColor dark:text-dark-mainColor" : "text-light-grayThree dark:text-dark-grayThree"}`}
									>
										{t("auth.register.roles.carrier.subtitle")}
									</P>
								</div>
							</div>

							<div
								className={`grid grid-cols-1 grid-flow-row auto-rows-max gap-4 place-items-center max-w-28`}
								onClick={() => setRoleID(6)}
							>
								<div
									className={`border p-4 rounded-xl cursor-pointer ${roleID === 6 ? "border-light-mainColor dark:border-dark-mainColor" : "border-light-stroke dark:border-dark-stroke"}`}
								>
									<SvgIcon
										Icon={CarrierThreeIcon}
										width={46}
										height={46}
										className={`${roleID === 6 ? "fill-light-mainColor dark:fill-dark-mainColor" : "fill-light-grayThree dark:fill-dark-grayThree"}`}
									/>
								</div>
								<div className="grid grid-flow-row grid-cols-1 gap-1">
									<P
										size="p3"
										type="semibold"
										className={`text-center ${roleID === 6 ? "text-light-mainColor dark:text-dark-mainColor" : "text-light-textColor dark:text-dark-textColor"}`}
									>
										{t("auth.register.roles.logisticsCompany.title")}
									</P>
									<P
										size="t1"
										type="regular"
										className={`text-center ${roleID === 6 ? "text-light-mainColor dark:text-dark-mainColor" : "text-light-grayThree dark:text-dark-grayThree"}`}
									>
										{t("auth.register.roles.logisticsCompany.subtitle")}
									</P>
								</div>
							</div>
						</div>
						<Button
							onClick={() => setStep(2)}
							appearance="primary"
							rounded="rounded-xl"
							width="full"
							disabled={!roleID}
						>
							{t("common.continue")}
						</Button>
					</div>
				) : step === 2 && roleID !== 3 && roleID !== 6 ? (
					<div className="grid grid-flow-row grid-cols-1 gap-5 auto-rows-max">
						<Htag tag="h3" type="semibold" className="text-center">
							{t("auth.register.whichCarrierType")}
						</Htag>

						<div className="grid gap-6 grid-cols-maxTwo place-items-center place-content-center">
							<div
								className={`grid grid-cols-1 grid-flow-row auto-rows-max gap-4 place-items-center max-w-28`}
								onClick={() => setRoleID(4)}
							>
								<div
									className={`border p-4 rounded-xl cursor-pointer ${roleID === 4 ? "border-light-mainColor dark:border-dark-mainColor" : "border-light-stroke dark:border-dark-stroke"}`}
								>
									<SvgIcon
										Icon={CarrierIcon}
										width={46}
										height={46}
										className={`${roleID === 4 ? "fill-light-mainColor dark:fill-dark-mainColor" : "fill-light-grayThree dark:fill-dark-grayThree"}`}
									/>
								</div>
								<div className="grid grid-flow-row grid-cols-1 gap-1">
									<P
										size="p3"
										type="semibold"
										className={`text-center ${roleID === 4 ? "text-light-mainColor dark:text-dark-mainColor" : "text-light-textColor dark:text-dark-textColor"}`}
									>
										{t("auth.register.roles.carrier.title")}
									</P>
									<P
										size="t1"
										type="regular"
										className={`text-center ${roleID === 4 ? "text-light-mainColor dark:text-dark-mainColor" : "text-light-grayThree dark:text-dark-grayThree"}`}
									>
										{t("auth.register.carrierTypes.personal")}
									</P>
								</div>
							</div>

							<div
								className={`grid grid-cols-1 grid-flow-row auto-rows-max gap-4 place-items-center max-w-28`}
								onClick={() => setRoleID(5)}
							>
								<div
									className={`border p-4 rounded-xl cursor-pointer ${roleID === 5 ? "border-light-mainColor dark:border-dark-mainColor" : "border-light-stroke dark:border-dark-stroke"}`}
								>
									<SvgIcon
										Icon={CarrierTwoIcon}
										width={46}
										height={46}
										className={`${roleID === 5 ? "fill-light-mainColor dark:fill-dark-mainColor" : "fill-light-grayThree dark:fill-dark-grayThree"}`}
									/>
								</div>
								<div className="grid grid-flow-row grid-cols-1 gap-1">
									<P
										size="p3"
										type="semibold"
										className={`text-center ${roleID === 5 ? "text-light-mainColor dark:text-dark-mainColor" : "text-light-textColor dark:text-dark-textColor"}`}
									>
										{t("auth.register.roles.carrier.title")}
									</P>
									<P
										size="t1"
										type="regular"
										className={`text-center ${roleID === 5 ? "text-light-mainColor dark:text-dark-mainColor" : "text-light-grayThree dark:text-dark-grayThree"}`}
									>
										{t("auth.register.carrierTypes.fleetOwner")}
									</P>
								</div>
							</div>
						</div>
						<Button
							onClick={() => setStep(3)}
							appearance="primary"
							rounded="rounded-xl"
							width="full"
							disabled={!roleID}
						>
							{t("common.continue")}
						</Button>
					</div>
				) : (step === 2 && (roleID === 3 || roleID === 6)) || step === 3 ? (
					<div className="grid grid-flow-row grid-cols-1 gap-5 auto-rows-max">
						<div className="grid grid-cols-3 gap-4 place-items-center">
							<hr className="w-full border-t-[3px] rounded border-light-mainColor dark:border-dark-mainColor" />
							<hr className="w-full border-t-[3px] rounded border-light-stroke dark:border-dark-stroke" />
							<hr className="w-full border-t-[3px] rounded border-light-stroke dark:border-dark-stroke" />
						</div>
						<P size="p1" type="semibold" className="text-center ">
							{usePhone
								? t("auth.register.enterPhone")
								: t("auth.register.enterEmail")}
						</P>

						<Input
							type={usePhone ? "tel" : "email"}
							label={
								usePhone
									? t("auth.register.phoneLabel")
									: t("auth.register.emailLabel")
							}
							value={usePhone ? phone : email}
							onChange={(e) =>
								usePhone ? setPhone(e.target.value) : setEmail(e.target.value)
							}
							placeholder={usePhone ? "+993" : "example@gmail.com"}
						/>
						<P
							size="p3"
							type="regular"
							className="text-center cursor-pointer"
							onClick={() => {
								setUsePhone((prev) => !prev);
								// optional: clear the other field to avoid confusion
								setPhone("");
								setEmail("");
							}}
						>
							{usePhone
								? t("auth.register.useEmail")
								: t("auth.register.usePhone")}
						</P>
						<div className="grid gap-6 place-items-center grid-cols-frMaxFr">
							<hr className="w-full border-t border-light-strokeLanding dark:border-dark-strokeLanding" />
							<P
								size="t1"
								type="semibold"
								className="text-light-grayThree dark:text-dark-grayThree"
							>
								{t("common.or")}
							</P>
							<hr className="w-full border-t border-light-strokeLanding dark:border-dark-strokeLanding" />
						</div>
						<div className="grid gap-3 grid-cols-maxTwo place-items-center place-content-center py-2.5 px-4 border border-light-strokeLanding dark:border-dark-strokeLanding rounded-xl cursor-pointer">
							<SvgIcon Icon={GoogleSignInIcon} width={24} height={24} />
							<P size="p3" type="semibold" className="cursor-pointer">
								{t("auth.register.continueWithGoogle")}
							</P>
						</div>
						<Checkbox
							label={
								<button
									type="button"
									className="text-left underline underline-offset-4 transition-colors duration-300 hover:text-light-mainColor dark:hover:text-dark-mainColor"
									onClick={(e) => {
										e.stopPropagation();
										onClose();
										navigate(ROUTES.TERMS);
									}}
								>
									{t("auth.register.agreeTerms")}
								</button>
							}
							checked={isChecked}
							onChange={(newValue: boolean) => setIsChecked(newValue)}
							toggleOnContainerClick={false}
						/>
						{isLoading ? (
							<div className="place-self-center">
								<Spinner size={24} />
							</div>
						) : (
							<Button
								type="button"
								onClick={handleRegisterRequest}
								appearance="primary"
								rounded="rounded-xl"
								width="full"
								disabled={
									!isChecked || !(usePhone ? phone.trim() : email.trim())
								}
							>
								{t("common.continue")}
							</Button>
						)}
					</div>
				) : (step === 3 && roleID === 3) || step === 4 ? (
					<div className="grid grid-flow-row grid-cols-1 gap-5 auto-rows-max">
						<div className="grid grid-cols-3 gap-4 place-items-center">
							<hr className="w-full border-t-[3px] rounded border-light-mainColor dark:border-dark-mainColor" />
							<hr className="w-full border-t-[3px] rounded border-light-mainColor dark:border-dark-mainColor" />
							<hr className="w-full border-t-[3px] rounded border-light-stroke dark:border-dark-stroke" />
						</div>

						<div className="grid grid-flow-row grid-cols-1 gap-1 auto-rows-max">
							<Htag tag="h3" type="semibold" className="text-center">
								{usePhone ? phone : email}
							</Htag>
							<P
								size="p3"
								type="regular"
								className="text-center text-light-grayThree dark:text-dark-grayThree"
							>
								{t("auth.register.otpSentTo", {
									target: usePhone
										? t("auth.register.target.phone")
										: t("auth.register.target.email"),
								})}
							</P>
						</div>

						<Input
							type="text"
							label={t("auth.register.otpLabel")}
							value={otp}
							onChange={(e) => setOtp(e.target.value)}
							placeholder={t("auth.register.otpPlaceholder")}
						/>

						{isLoading ? (
							<div className="place-self-center">
								<Spinner size={24} />
							</div>
						) : (
							<Button
								onClick={handleValidateOtp}
								appearance="primary"
								rounded="rounded-xl"
								width="full"
								disabled={!otp}
							>
								{t("common.continue")}
							</Button>
						)}
					</div>
				) : step === 4 ? (
					<>
						<Input
							type="text"
							label={t("auth.register.otpLabel")}
							value={otp}
							onChange={(e) => setOtp(e.target.value)}
						/>
						<Button
							type="button"
							onClick={handleValidateOtp}
							disabled={!otp}
							appearance="primary"
							width="full"
						>
							{t("auth.register.confirm")}
						</Button>
					</>
				) : (
					<div className="grid grid-flow-row grid-cols-1 gap-5 auto-rows-max">
						<div className="grid grid-cols-3 gap-4 place-items-center">
							<hr className="w-full border-t-[3px] rounded border-light-mainColor dark:border-dark-mainColor" />
							<hr className="w-full border-t-[3px] rounded border-light-mainColor dark:border-dark-mainColor" />
							<hr className="w-full border-t-[3px] rounded border-light-mainColor dark:border-dark-mainColor" />
						</div>

						<Htag tag="h3" type="semibold" className="text-center">
							{t("auth.register.createPasswordTitle")}
						</Htag>

						<Input
							type="password"
							label={t("auth.register.passwordCreateLabel")}
							value={password}
							onChange={(e) => setPassword(e.target.value)}
							placeholder={t("auth.register.passwordCreatePlaceholder")}
						/>

						<Input
							type="password"
							label={t("auth.register.passwordConfirmLabel")}
							value={confirmPassword}
							onChange={(e) => setConfirmPassword(e.target.value)}
							placeholder={t("auth.register.passwordConfirmPlaceholder")}
						/>

						{isLoading ? (
							<div className="place-self-center">
								<Spinner size={24} />
							</div>
						) : (
							<Button
								type="button"
								onClick={handleFinalRegistration}
								appearance="primary"
								rounded="rounded-xl"
								width="full"
								disabled={
									isLoading ||
									!password ||
									!confirmPassword ||
									password !== confirmPassword
								}
								icon={
									isLoading ? (
										<Spinner
											size={18}
											color="text-light-grayOne dark:text-dark-grayOne"
										/>
									) : undefined
								}
							>
								{isLoading ? t("common.loading") : t("common.continue")}
							</Button>
						)}
					</div>
				)}
			</motion.div>
		),
		[
			step,
			email,
			password,
			roleID,
			otp,
			isLoading,
			isChecked,
			usePhone,
			phone,
			handleRegisterRequest,
			handleValidateOtp,
			handleFinalRegistration,
			t,
			confirmPassword,
		],
	);

	return (
		<Modal
			isOpen={isModalOpen}
			onClose={onClose}
			header={{
				center: <SvgIcon Icon={LogoLightNavIcon} width={86} height={32} />,
			}}
			containerClassName="max-h-[calc(100vh-5vh)]"
		>
			{renderStep}
		</Modal>
	);
};

export default React.memo(RegisterProcess);
